import { toLocalDate } from './local-date.js';
/**
 * The calendar day an instant falls on **in a named timezone**.
 *
 * ## R-1e, and what this does and does not close
 *
 * Every date comparison in this package is already correct: `LocalDate` is a
 * calendar fact and the comparisons are integer arithmetic. R-1e is not about
 * comparison. It is about the single line that turns a clock reading into the
 * day being compared, and every one of those in the API does it in UTC:
 *
 *     now.toISOString().slice(0, 10)
 *
 * For an agency in `America/New_York` that line rolls over at 7pm local in
 * winter and 8pm in summer. From then until midnight the product believes it is
 * tomorrow: payer rules lapse, authorizations expire, and — once credential
 * compliance ships — a caregiver is refused a shift they are qualified for. The
 * error is always in the same direction, coverage ending too early, which is the
 * direction that stops care rather than the one that overpays for it.
 *
 * This function is the correct line. Giving the three UTC call sites something
 * right to call is a precondition for closing R-1e; it is not the closure. That
 * happens when `coverage-window.ts`, `authorizations.service.ts` and every new
 * caller use it, which has to be done to all of them together — a rule that had
 * lapsed while its own authorization still read ACTIVE is worse than both being
 * uniformly early, because then two screens disagree and neither is obviously
 * wrong.
 *
 * ## Why `Intl` is allowed here
 *
 * `packages/domain` is pure — zero I/O, zero framework imports — and `Intl` is
 * neither. It is a pure function from an instant and a zone name to fields,
 * using the tz database the runtime already carries. No clock is read: the
 * instant is an argument. The one impurity would be `new Date()`, which does not
 * appear.
 *
 * Doing the arithmetic by hand instead would mean shipping an offset table and
 * a DST rule set, and being wrong the first time a legislature moves a
 * transition — which happens somewhere every year.
 */
/**
 * `en-CA` renders as `YYYY-MM-DD`, which is what `toLocalDate` wants.
 *
 * Formatters are expensive to build and are constructed once per zone. An
 * agency has one primary timezone, so this cache holds a handful of entries for
 * the life of the process.
 */
const formatters = new Map();
export class UnknownTimeZoneError extends Error {
    timeZone;
    constructor(timeZone) {
        super(`"${timeZone}" is not an IANA timezone this runtime knows. ` +
            'Expected something like "America/New_York" — agency.primary_timezone is ' +
            'the source, and a value it cannot resolve means the agency was created ' +
            'with an unvalidated zone.');
        this.timeZone = timeZone;
        this.name = 'UnknownTimeZoneError';
    }
}
function formatterFor(timeZone) {
    const cached = formatters.get(timeZone);
    if (cached)
        return cached;
    let formatter;
    try {
        formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
    }
    catch {
        // `Intl` throws RangeError for an unknown zone. Rethrown as something that
        // names the column the bad value came from, because "Invalid time zone
        // specified" on its own has sent people looking at the wrong system.
        throw new UnknownTimeZoneError(timeZone);
    }
    formatters.set(timeZone, formatter);
    return formatter;
}
/**
 * The calendar day `instant` falls on in `timeZone`.
 *
 * @param instant an absolute time — `clock.now()`, never `new Date()` at a call
 *   site, so the caller stays testable
 * @param timeZone an IANA name, normally `agency.primary_timezone`
 */
export function agencyLocalDay(instant, timeZone) {
    const rendered = formatterFor(timeZone).format(instant);
    // `en-CA` gives YYYY-MM-DD, but that is a locale convention rather than a
    // specification, so the result is validated rather than trusted: `toLocalDate`
    // throws `InvalidLocalDateError` on anything else. A runtime that rendered it
    // differently would fail here, loudly, instead of writing a date nobody can
    // parse into an audit row.
    //
    // No try/catch around it — catching and rethrowing the same error type is
    // ceremony, and it is a branch no test can reach because no runtime this
    // product supports renders `en-CA` any other way.
    return toLocalDate(rendered);
}
//# sourceMappingURL=agency-day.js.map